import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  Alert,
  Stack,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
  Divider,
  LinearProgress,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Checkbox,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Snackbar,
  Slider,
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  AutoAwesome as AIIcon,
  NavigateBefore,
  NavigateNext,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Save as SaveIcon,
  Code as JsonIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Pages as PagesIcon,
  WarningAmber as WarningIcon,
  DeleteOutline as DeleteIcon,
  Cancel as CancelIcon,
  EventNote as PlanIcon,
  ArrowRightAlt as GoLayoutIcon,
  SmartToy as AgentIcon,
} from '@mui/icons-material';
import albumService from '../../api/albumService';
import { io } from 'socket.io-client';
import AuthenticatedImage from '../AuthenticatedImage';
import { getServerUrl } from '../../config/runtimeConfig';

const API_URL = getServerUrl();

// ── Plan spread-map constants ─────────────────────────────────────────────────
const ROLE_COLORS = {
  opening:    '#7b1fa2',
  buildup:    '#1565c0',
  climax:     '#c62828',
  transition: '#558b2f',
  close:      '#ef6c00',
};

// Block fill color keyed by scene budget (page count)
const BUDGET_COLORS = {
  1: '#90a4ae', // 1p — brief/transition — grey-blue
  2: '#42a5f5', // 2p — standard spread — blue
  3: '#ffa726', // 3p — extended — amber
  4: '#ef5350', // 4p — epic — red
};

const LAYOUT_STYLE_META = {
  condensed: { label: 'Condensed', color: '#5c6bc0', icon: '⊞', desc: 'Many images per page — dense collage / magazine style' },
  balanced:  { label: 'Balanced',  color: '#00897b', icon: '▪', desc: 'Mix of hero + supporting images — standard album feel'  },
  relaxed:   { label: 'Relaxed',   color: '#fb8c00', icon: '○', desc: '1–2 images with white space — intimate / editorial'       },
};

/**
 * SpreadMap — horizontal strip visualising how scenes fill album spreads.
 * Each scene is shown as a colored band whose width is proportional to its budget.
 * Spread boundaries are marked with vertical dividers.
 */
const SpreadMap = ({ scenePlan, storyScenes, highlightSceneId, onSceneClick }) => {
  if (!scenePlan?.length) return null;
  const sceneMap = new Map((storyScenes || []).map(s => [s.sceneId, s]));

  // Build the physical page-to-scene mapping
  const pageSlots = []; // [{ sceneId, budget, role, sceneName, emoji, isFirst, isLast }]
  let page = 1;
  for (const entry of scenePlan) {
    const budget = Math.max(1, entry.budget || 1);
    const scene = sceneMap.get(entry.sceneId);
    for (let p = 0; p < budget; p++) {
      pageSlots.push({
        sceneId:   entry.sceneId,
        budget,
        role:      entry.role || 'transition',
        sceneName: entry.sceneName || entry.combined_scene_name || scene?.sceneName || entry.sceneId,
        emoji:     scene?.emoji || '📷',
        isFirst:   p === 0,
        isLast:    p === budget - 1,
        pageNum:   page + p,
      });
    }
    page += budget;
  }

  // Group into physical spreads (spread 0 = blank + p1; spread n = p2n + p2n+1)
  const spreads = [];
  // spread 0 is the blank cover + page 1
  const allPhysicalPages = [null, ...pageSlots]; // prepend blank cover
  const totalPhysical = allPhysicalPages.length % 2 === 0 ? allPhysicalPages.length : allPhysicalPages.length + 1;
  for (let i = 0; i < totalPhysical; i += 2) {
    spreads.push({
      spreadNum: i / 2,
      left:  allPhysicalPages[i]  || null,
      right: allPhysicalPages[i + 1] || null,
    });
  }

  const SLOT_W = 28; // px per page slot
  const SLOT_H = 48;
  const GAP    = 2;

  return (
    <Box sx={{ overflowX: 'auto', pb: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, ml: 0.5 }}>
        Album spread map — <strong>{spreads.length}</strong> spreads · <strong>{page - 1}</strong> pages
        &nbsp;(click a spread to highlight its scene in the table below)
      </Typography>
      <Stack direction="row" spacing={`${GAP}px`} alignItems="flex-end" sx={{ minWidth: 'max-content', px: 0.5 }}>
        {spreads.map((sp, si) => {
          const renderSlot = (slot, side) => {
            if (!slot) {
              return (
                <Box
                  key={side}
                  sx={{
                    width: SLOT_W, height: SLOT_H,
                    bgcolor: '#e8e8e8',
                    borderRadius: side === 'left' ? '2px 0 0 2px' : '0 2px 2px 0',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <Typography variant="caption" sx={{ fontSize: 8, color: '#aaa' }}>—</Typography>
                </Box>
              );
            }
            const roleColor   = ROLE_COLORS[slot.role] || '#90a4ae';
            const budgetColor = BUDGET_COLORS[slot.budget] || '#90a4ae';
            const isHighlighted = slot.sceneId === highlightSceneId;
            return (
              <Tooltip
                key={`${si}-${side}`}
                placement="top"
                title={
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                      {slot.emoji} {slot.sceneName}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      Role: {slot.role} · Budget: {slot.budget}p · Page: {slot.pageNum}
                    </Typography>
                    {slot.isFirst && (
                      <Typography variant="caption" sx={{ display: 'block', color: '#90caf9' }}>
                        ← Scene starts here
                      </Typography>
                    )}
                  </Box>
                }
              >
                <Box
                  onClick={() => onSceneClick?.(slot.sceneId)}
                  sx={{
                    width: SLOT_W, height: SLOT_H,
                    bgcolor: isHighlighted ? budgetColor : `${budgetColor}55`,
                    borderTop: `3px solid ${roleColor}`,
                    borderBottom: `1px solid ${budgetColor}88`,
                    borderLeft: slot.isFirst ? `3px solid ${budgetColor}` : `1px solid ${budgetColor}44`,
                    borderRight: slot.isLast  ? `3px solid ${budgetColor}` : `1px solid ${budgetColor}44`,
                    borderRadius: side === 'left' ? '2px 0 0 2px' : '0 2px 2px 0',
                    cursor: 'pointer',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    gap: 0,
                    transition: 'background .15s',
                    '&:hover': { bgcolor: `${budgetColor}99` },
                  }}
                >
                  {slot.isFirst && (
                    <Typography sx={{ fontSize: 9, lineHeight: 1, color: isHighlighted ? '#fff' : budgetColor, fontWeight: 700 }}>
                      {slot.emoji}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: 8, lineHeight: 1.2, color: isHighlighted ? '#fff' : budgetColor, fontWeight: 600 }}>
                    {slot.pageNum}
                  </Typography>
                  {slot.isFirst && (
                    <Box sx={{
                      mt: 0.25,
                      px: 0.4, py: 0,
                      bgcolor: isHighlighted ? 'rgba(255,255,255,0.25)' : `${budgetColor}cc`,
                      borderRadius: '3px',
                      lineHeight: 1.4,
                    }}>
                      <Typography sx={{ fontSize: 7, fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>
                        {slot.budget}p
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Tooltip>
            );
          };

          const crossScene = sp.left && sp.right && sp.left.sceneId !== sp.right.sceneId;
          return (
            <Box key={si} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
              {/* spread card */}
              <Box
                sx={{
                  display: 'flex',
                  border: '1px solid',
                  borderColor: crossScene ? 'warning.main' : 'grey.300',
                  borderRadius: 1,
                  overflow: 'hidden',
                  boxShadow: 0,
                  position: 'relative',
                }}
              >
                {/* center fold line */}
                <Box sx={{ position: 'absolute', left: SLOT_W - 0.5, top: 0, bottom: 0, width: 1, bgcolor: 'rgba(0,0,0,0.15)', zIndex: 1 }} />
                {renderSlot(sp.left, 'left')}
                {renderSlot(sp.right, 'right')}
              </Box>
              {/* spread number — 1-based to match preview & layout debug */}
              <Typography variant="caption" sx={{ fontSize: 8, color: 'text.disabled', lineHeight: 1 }}>
                S{sp.spreadNum + 1}
              </Typography>
              {crossScene && (
                <Typography variant="caption" sx={{ fontSize: 7, color: 'warning.main', lineHeight: 1 }}>
                  ⚠ cross
                </Typography>
              )}
            </Box>
          );
        })}
      </Stack>
      {/* Legends */}
      <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: 'wrap' }} useFlexGap>
        {/* Budget legend */}
        <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ fontSize: 9, color: 'text.disabled', mr: 0.25 }}>pages:</Typography>
          {Object.entries(BUDGET_COLORS).map(([budget, color]) => (
            <Stack key={budget} direction="row" alignItems="center" spacing={0.4}>
              <Box sx={{ width: 10, height: 10, bgcolor: color, borderRadius: '2px', flexShrink: 0 }} />
              <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary' }}>{budget}p</Typography>
            </Stack>
          ))}
        </Stack>
        {/* Role legend */}
        <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ fontSize: 9, color: 'text.disabled', mr: 0.25 }}>role (top border):</Typography>
          {Object.entries(ROLE_COLORS).map(([role, color]) => (
            <Stack key={role} direction="row" alignItems="center" spacing={0.4}>
              <Box sx={{ width: 14, height: 4, bgcolor: color, borderRadius: '1px', flexShrink: 0 }} />
              <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary', textTransform: 'capitalize' }}>{role}</Typography>
            </Stack>
          ))}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.4}>
          <Box sx={{ width: 10, height: 10, border: '1px solid', borderColor: 'warning.main', borderRadius: '2px', flexShrink: 0 }} />
          <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary' }}>cross-scene spread</Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

// ── A4 landscape scale ────────────────────────────────────────────────────────
// 297mm × 210mm → displayed at SCALE pixels/mm
const PREVIEW_W_PX = 794; // ~297mm at 96dpi
const SCALE = PREVIEW_W_PX / 297;
const PREVIEW_H_PX = Math.round(210 * SCALE); // ~561px

// ── Page Preview ─────────────────────────────────────────────────────────────
const PagePreview = ({ page, backgroundColor, pageMarginMm = 0 }) => {
  const bg = page.backgroundColor || backgroundColor || '#f8f5f0';
  const marginPx = Math.round(pageMarginMm * SCALE);
  return (
    <Box
      sx={{
        position: 'relative',
        width: PREVIEW_W_PX,
        height: PREVIEW_H_PX,
        backgroundColor: bg,
        overflow: 'hidden',
        border: '2px solid',
        borderColor: 'grey.400',
        boxShadow: 4,
        flexShrink: 0,
      }}
    >
      {/* Image slots */}
      {(page.images || []).map((slot, si) => {
        const x      = Math.round(slot.x * SCALE);
        const y      = Math.round(slot.y * SCALE);
        const w      = Math.round(slot.width * SCALE);
        const h      = Math.round(slot.height * SCALE);
        const zoom   = slot.zoom || 1;
        const focusX = slot.focusX ?? 0.5;  // 0–1 image-space
        const focusY = slot.focusY ?? 0.5;
        const radius = Math.round((slot.borderRadius || 0) * SCALE);
        const imageId = slot._meta?.imageId || slot.imageId;

        // Thumbnails are stored as 300×300 squares (center-cropped from original).
        // When we know origW/origH we can convert focusX/Y from original-image space
        // into thumbnail space, then compute the exact objectPosition needed to center
        // the face in the slot using CSS objectFit:cover.
        const origW = slot._meta?.origWidth || 0;
        const origH = slot._meta?.origHeight || 0;
        let imgStyle;
        if (origW > 0 && origH > 0) {
          // Convert focus point from original-image space to thumbnail (square crop) space.
          // Portrait original: square crop removes (origH-origW)/2 from top & bottom.
          // Landscape original: square crop removes (origW-origH)/2 from left & right.
          let thumbFocusX = focusX;
          let thumbFocusY = focusY;
          if (origH > origW) {
            thumbFocusY = Math.max(0, Math.min(1,
              (origH / origW * (2 * focusY - 1) + 1) / 2
            ));
          } else if (origW > origH) {
            thumbFocusX = Math.max(0, Math.min(1,
              (origW / origH * (2 * focusX - 1) + 1) / 2
            ));
          }
          // With a 1:1 thumbnail and objectFit:cover:
          //   landscape slot (w >= h): renders thumbnail as w×w, overflow = w-h in Y
          //   portrait slot  (h > w):  renders thumbnail as h×h, overflow = h-w in X
          // objectPosition Y% moves the visible window by overflowY * Y% from the top.
          let objX = 50;
          let objY = 50;
          if (w >= h) {
            const overflowY = w - h;
            objY = overflowY > 0
              ? Math.max(0, Math.min(100, (thumbFocusY * w - h / 2) / overflowY * 100))
              : thumbFocusY * 100;
          } else {
            const overflowX = h - w;
            objX = overflowX > 0
              ? Math.max(0, Math.min(100, (thumbFocusX * h - w / 2) / overflowX * 100))
              : thumbFocusX * 100;
          }
          if (zoom > 1) {
            // Panorama / explicitly-zoomed slot: position the image at zoom×size with
            // thumbFocusX/Y determining which crop is visible (same math as Preview.jsx).
            const minOff = -(zoom - 1) * 100;
            const zl = Math.max(minOff, Math.min(0, (0.5 - thumbFocusX * zoom) * 100));
            const zt = Math.max(minOff, Math.min(0, (0.5 - thumbFocusY * zoom) * 100));
            imgStyle = {
              position: 'absolute',
              width: `${zoom * 100}%`,
              height: `${zoom * 100}%`,
              objectFit: 'cover',
              objectPosition: `${thumbFocusX * 100}% ${thumbFocusY * 100}%`,
              left: `${zl}%`,
              top: `${zt}%`,
              display: 'block',
            };
          } else {
            imgStyle = {
              position: 'absolute',
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: `${objX.toFixed(1)}% ${objY.toFixed(1)}%`,
              display: 'block',
            };
          }
        } else {
          // Fallback for older stored layouts without known dimensions
          imgStyle = {
            position: 'absolute',
            width: `${zoom * 100}%`,
            height: `${zoom * 100}%`,
            objectFit: 'cover',
            objectPosition: `${focusX * 100}% ${focusY * 100}%`,
            left: `${-(zoom - 1) * focusX * 100}%`,
            top: `${-(zoom - 1) * focusY * 100}%`,
            display: 'block',
          };
        }

        return (
          <Box
            key={si}
            sx={{
              position: 'absolute',
              left: x,
              top: y,
              width: w,
              height: h,
              overflow: 'hidden',
              zIndex: slot.zIndex ?? 0,
              borderRadius: `${radius}px`,
              bgcolor: 'grey.200',
              boxSizing: 'border-box',
            }}
          >
            {imageId ? (
              <AuthenticatedImage
                imageId={imageId}
                type="thumbnail"
                fallbackToServe
                alt={slot._meta?.description || ''}
                sx={imgStyle}
              />
            ) : (
              <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center', px: 0.5 }}>
                  {slot.imageId?.substring(0, 8) ?? 'no id'}
                </Typography>
              </Box>
            )}
            {/* Focus-override badge: shown when backend corrected the model's focusX/Y using the real face bbox */}
            {slot._meta?._focusOverridden && (
              <Box
                title={`Focus overridden by backend face detection.\nModel: focusX=${slot._meta._focusOriginal?.focusX?.toFixed(2)}, focusY=${slot._meta._focusOriginal?.focusY?.toFixed(2)}\nCorrected: focusX=${slot.focusX?.toFixed(2)}, focusY=${slot.focusY?.toFixed(2)}`}
                sx={{
                  position: 'absolute',
                  bottom: 2,
                  right: 2,
                  bgcolor: 'warning.main',
                  color: 'warning.contrastText',
                  fontSize: 7,
                  lineHeight: 1,
                  px: 0.4,
                  py: 0.3,
                  borderRadius: 0.5,
                  pointerEvents: 'auto',
                  cursor: 'help',
                  zIndex: 10,
                  userSelect: 'none',
                }}
              >
                face↑
              </Box>
            )}
          </Box>
        );
      })}

      {/* Text blocks */}
      {(page.textBlocks || []).map((tb, ti) => (
        <Box
          key={ti}
          sx={{
            position: 'absolute',
            left: Math.round(tb.x * SCALE),
            top: Math.round(tb.y * SCALE),
            width: Math.round(tb.width * SCALE),
            zIndex: 20,
            pointerEvents: 'none',
          }}
        >
          <Typography
            sx={{
              fontSize: Math.round(tb.fontSize * SCALE * 0.6),
              fontWeight: tb.fontWeight || 'normal',
              color: tb.color || '#333',
              textAlign: tb.align || 'left',
              lineHeight: 1.2,
              textShadow: '0 1px 3px rgba(255,255,255,0.7)',
            }}
          >
            {tb.text}
          </Typography>
        </Box>
      ))}

      {/* Margin guide overlay */}
      {pageMarginMm > 0 && (
        <Box
          sx={{
            position: 'absolute',
            left: marginPx,
            top: marginPx,
            width: PREVIEW_W_PX - marginPx * 2,
            height: PREVIEW_H_PX - marginPx * 2,
            border: '1px dashed rgba(30,100,255,0.40)',
            pointerEvents: 'none',
            zIndex: 50,
            boxSizing: 'border-box',
          }}
        />
      )}

      {/* Page index badge */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 4,
          right: 6,
          zIndex: 30,
          pointerEvents: 'none',
        }}
      >
        <Typography sx={{ fontSize: 9, color: 'rgba(0,0,0,0.3)', fontWeight: 500 }}>
          Page {(page.pageIndex ?? 0) + 1}
        </Typography>
      </Box>

      {/* Layout template label */}
      {page.layoutTemplateName && (
        <Box sx={{ position: 'absolute', top: 4, left: 6, zIndex: 30, pointerEvents: 'none' }}>
          <Typography sx={{ fontSize: 9, color: 'rgba(0,0,0,0.3)', fontWeight: 500 }}>
            {page.layoutTemplateName}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ── Scene Layout Card ─────────────────────────────────────────────────────────
const SceneLayoutCard = ({ sceneLayout, scene, isSelected, onSelect, pageMarginMm = 0 }) => {
  const pages = sceneLayout.pages || [];
  const [expanded, setExpanded] = useState(true);

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 2,
        overflow: 'hidden',
        border: isSelected ? '2px solid' : '1px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        cursor: 'pointer',
      }}
      onClick={onSelect}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2, py: 1,
          display: 'flex', alignItems: 'center', gap: 1,
          bgcolor: isSelected ? 'primary.50' : 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
        onClick={(e) => { e.stopPropagation(); setExpanded(v => !v); }}
      >
        <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 600 }}>
          {scene?.emoji} {sceneLayout.sceneId} — {scene?.sceneName || sceneLayout.sceneId}
        </Typography>
        {sceneLayout.templatePairName && (
          <Chip
            label={sceneLayout.templatePairName}
            size="small"
            variant="outlined"
            sx={{ height: 20, fontSize: 10, fontFamily: 'monospace' }}
            title="Template pair used"
          />
        )}
        {scene?.themeRelevanceScore != null && (
          <Chip
            label={`★ ${scene.themeRelevanceScore}/10`}
            size="small"
            color={scene.themeRelevanceScore >= 8 ? 'success' : scene.themeRelevanceScore >= 5 ? 'warning' : 'error'}
            sx={{ height: 20 }}
          />
        )}
        <Chip label={`${pages.length}p`} size="small" sx={{ height: 20 }} />
        {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </Box>

      {/* Pages */}
      <Collapse in={expanded}>
        <Box sx={{ p: 2, display: 'flex', gap: 2, overflowX: 'auto' }}>
          {pages.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No pages in this layout</Typography>
          ) : (
            pages.map((page, pi) => (
              <Box key={pi}>
                <Box sx={{ transform: 'scale(0.45)', transformOrigin: 'top left', mb: `-${Math.round(PREVIEW_H_PX * 0.55 + 4)}px`, mr: `-${Math.round(PREVIEW_W_PX * 0.55 + 4)}px` }}>
                  <PagePreview page={page} backgroundColor={sceneLayout.backgroundColor} pageMarginMm={pageMarginMm} />
                </Box>
              </Box>
            ))
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const WizardStep6Layout = ({ session, onNext, onBack, onNavConfigChange }) => {
  const sessionId = session?.sessionId || session?.uniqueId;

  // Layout state
  const [layouts, setLayouts] = useState([]);
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const showSnackbar = (message, severity = 'info') =>
    setSnackbar({ open: true, message, severity });
  const [genError, setGenError] = useState(null);
  const [failedScenes, setFailedScenes] = useState([]);
  const [tokenStats, setTokenStats] = useState(null);

  // UI state
  const [selectedSceneId, setSelectedSceneId] = useState(null);
  const [showJson, setShowJson] = useState(false);
  const [editedJson, setEditedJson] = useState('');
  const [jsonError, setJsonError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmRegenOpen, setConfirmRegenOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  // Generation progress (updated by socket events; polling provides fallback for environments
  // where WebSocket delivery is unreliable, e.g. behind certain proxies)
  const [genProgress, setGenProgress] = useState({ current: 0, total: 0, lastSceneMs: null, lastSceneName: '' });
  const genProgressRef = useRef(null); // holds the polling interval id
  const socketRef = useRef(null);       // Socket.IO connection for layout events

  const [cancelling, setCancelling] = useState(false);
  const [alreadyRunningOpen, setAlreadyRunningOpen] = useState(false);
  const [runningJobInfo, setRunningJobInfo] = useState(null);

  // Settings
  const [sceneLimitEnabled, setSceneLimitEnabled] = useState(false); // when false → process all scenes
  const [debugSceneLimit, setDebugSceneLimit] = useState(2);  // only used when sceneLimitEnabled
  const [pageMarginMm, setPageMarginMm] = useState(5); // mm of empty border to keep around page edges
  const [albumContext, setAlbumContext] = useState('');
  const [allowCrossSceneImages, setAllowCrossSceneImages] = useState(true);
  const [scenesPerIteration, setScenesPerIteration] = useState(2);
  const [showTitleStrip, setShowTitleStrip] = useState(false); // default: no title inside album pages
  const [aiModel, setAiModel] = useState('gpt-6.1-sol');
  const [availableModels, setAvailableModels] = useState([]);
  const [useLayoutAgent, setUseLayoutAgent] = useState(true);
  const [usePlanAgent, setUsePlanAgent] = useState(true);
  const [planTemperature, setPlanTemperature] = useState(0.4);
  const [peopleVsScenery, setPeopleVsScenery] = useState(5); // 1=all scenery … 10=all people, default 5 balanced
  const [layoutTemperature, setLayoutTemperature] = useState(0.7);
  const [budgetConfig, setBudgetConfig] = useState({
    budget2MinScore: 9,
    budget2MinImages: 5,
    budget3MinScore: 9,
    budget3MinImages: 12,
    budget3MinClusters: 4,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    onNavConfigChange?.({
      nextLabel: 'Continue to Preview',
      nextDisabled: generating || layouts.length === 0,
      backDisabled: generating,
      showSkip: false,
    });
  }, [generating, layouts.length]); // onNavConfigChange is stable
  const [includeAllQuality, setIncludeAllQuality] = useState(false); // when false → filter blurry/not-worthy
  const [debugFileName, setDebugFileName] = useState('');
  const [selectedSceneIds, setSelectedSceneIds] = useState(null); // null = not yet initialised
  const [minRateFilter, setMinRateFilter] = useState(0); // 0 = show all ratings
  const [layoutSceneTableMode, setLayoutSceneTableMode] = useState('plan'); // 'plan' | 'raw'

  // Album template collection selector
  const [albumTemplates, setAlbumTemplates] = useState([]); // DB collections
  const [selectedAlbumTemplateId, setSelectedAlbumTemplateId] = useState(''); // set after load

  // Plan tab state
  const [activeTab, setActiveTab] = useState(0); // 0 = Plan, 1 = Layout
  const [albumPlan, setAlbumPlan] = useState(null);
  const [planGenerating, setPlanGenerating] = useState(false);
  const [planError, setPlanError] = useState(null);
  const [planDebugFileName, setPlanDebugFileName] = useState('');
  const [planRowExpanded, setPlanRowExpanded] = useState({}); // { [sceneId]: true }
  const [planHighlightSceneId, setPlanHighlightSceneId] = useState(null); // highlighted in SpreadMap
  const [showPlanAgentTranscript, setShowPlanAgentTranscript] = useState(false);
  const [expandedLayoutAgentRuns, setExpandedLayoutAgentRuns] = useState({}); // { [runKey]: true }
  const [liveAgentRunsByScene, setLiveAgentRunsByScene] = useState({}); // { [sceneId]: run[] }

  // Elapsed time counter for plan generation spinner
  const planStartTimeRef = useRef(null);
  const [planElapsedSec, setPlanElapsedSec] = useState(0);
  useEffect(() => {
    if (!planGenerating) {
      planStartTimeRef.current = null;
      setPlanElapsedSec(0);
      return;
    }
    planStartTimeRef.current = Date.now();
    const interval = setInterval(() => {
      if (planStartTimeRef.current) {
        setPlanElapsedSec(Math.floor((Date.now() - planStartTimeRef.current) / 1000));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [planGenerating]);

  // ── Load on mount ───────────────────────────────────────────────────────────
  useEffect(() => {
    albumService.getAlbumTemplates().then(data => {
      const templates = data?.templates || [];
      setAlbumTemplates(templates);
      // Default to "Standard Built-in Templates" collection
      const standard = templates.find(t => t.name === 'Standard Built-in Templates');
      if (standard) setSelectedAlbumTemplateId(standard._id);
      else if (templates.length > 0) setSelectedAlbumTemplateId(templates[0]._id);
    }).catch(() => {});
    // Load available AI models
    albumService.getSupportedAiModels().then(data => {
      if (data?.models) setAvailableModels(data.models);
      if (data?.defaultModel) setAiModel(data.defaultModel);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const [storyData, layoutData, planData] = await Promise.all([
          albumService.getStoryTimeline(sessionId).catch(() => null),
          albumService.getAlbumLayout(sessionId).catch(() => null),
          albumService.getAlbumPlan(sessionId).catch(() => null),
        ]);
        if (storyData?.storyTimeline) {
          setStory(storyData.storyTimeline);
          setAlbumContext(String(storyData.storyTimeline?.storySettings?.userContext || ''));
          // Pre-select scenes without a layout yet (or previously failed); prefer plan order/IDs when a plan is already loaded
          const existingIds = new Set((layoutData?.albumLayout?.layouts || []).map(l => l.sceneId));
          const failedIds = new Set((layoutData?.albumLayout?.failedScenes || []).map(f => f.sceneId));
          const planEntries = planData?.albumPlan?.scene_plan;
          const sourceScenes = planEntries?.length
            ? planEntries.map(e => ({ sceneId: e.sceneId }))
            : (storyData.storyTimeline.scenes || []).filter(s => s.includedInAlbum !== false);
          setSelectedSceneIds(new Set(sourceScenes.filter(s => !existingIds.has(s.sceneId) || failedIds.has(s.sceneId)).map(s => s.sceneId)));
        }
        if (layoutData?.albumLayout?.layouts?.length) {
          setLayouts(layoutData.albumLayout.layouts);
          setSelectedSceneId(prev => prev || layoutData.albumLayout.layouts[0]?.sceneId || null);
          if (layoutData.albumLayout.tokenStats) setTokenStats(layoutData.albumLayout.tokenStats);
          if (layoutData.albumLayout.debugFileName) setDebugFileName(layoutData.albumLayout.debugFileName);
          if (layoutData.albumLayout.failedScenes?.length) setFailedScenes(layoutData.albumLayout.failedScenes);
        } else if (layoutData?.albumLayout?.failedScenes?.length) {
          setFailedScenes(layoutData.albumLayout.failedScenes);
        }
        // Detect an in-progress job (e.g. page refresh during generation)
        const jobStatus = layoutData?.albumLayout?.jobStatus;
        if (jobStatus?.status === 'running') {
          const included = (storyData?.storyTimeline?.scenes || []).filter(s => s.includedInAlbum !== false);
          const runningTotal = jobStatus.total || included.length;
          const startedAt = jobStatus.startedAt ? new Date(jobStatus.startedAt).getTime() : Date.now();
          setGenerating(true);
          setRunningJobInfo(jobStatus);
          setGenProgress({ current: 0, total: runningTotal, lastSceneMs: null, lastSceneName: '', startedAt });
        }
        // Load plan
        if (planData?.albumPlan) {
          setAlbumPlan(planData.albumPlan);
          if (planData.albumPlan.debugFileName) setPlanDebugFileName(planData.albumPlan.debugFileName);
        }
      } catch (err) {
        setLoadError(err.message || 'Failed to load layout data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [sessionId]);

  // ── Selected layout tracking ───────────────────────────────────────────────
  const selectedLayout = useMemo(
    () => layouts.find(l => l.sceneId === selectedSceneId),
    [layouts, selectedSceneId]
  );
  const selectedScene = useMemo(
    () => story?.scenes?.find(s => s.sceneId === selectedSceneId),
    [story, selectedSceneId]
  );

  const selectedLayoutAgentRuns = useMemo(() => {
    const sceneId = selectedSceneId || selectedLayout?.sceneId;
    if (!sceneId) return [];

    const persistedRuns = selectedLayout
      ? (Array.isArray(selectedLayout.layoutAgentDebugRuns)
          ? selectedLayout.layoutAgentDebugRuns
          : (selectedLayout.layoutAgentDebug ? [selectedLayout.layoutAgentDebug] : []))
      : [];
    const liveRuns = liveAgentRunsByScene[sceneId] || [];
    const merged = [...persistedRuns, ...liveRuns];

    const seen = new Set();
    const out = [];
    merged.forEach((run, idx) => {
      const key = String(run.runId || `${sceneId}-run-${idx}`);
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ ...run, _runKey: key });
    });
    return out;
  }, [selectedLayout, selectedSceneId, liveAgentRunsByScene]);

  useEffect(() => {
    if (selectedLayout) {
      setEditedJson(JSON.stringify(selectedLayout, null, 2));
      setJsonError(null);
    }
  }, [selectedLayout]);

  // ── Socket.IO — connect on mount, disconnect on unmount ─────────────────────
  useEffect(() => {
    if (!sessionId) return;
    const socket = io(API_URL, { path: '/socket.io/', withCredentials: true });

    socket.on('connect', () => {
      console.log('[Step6] Layout socket connected:', socket.id);
      socket.emit('joinLayoutRoom', { sessionId });
    });

    socket.on('layoutProgress', (data) => {
      if (data.sessionId !== sessionId) return;
      setGenProgress(prev => ({
        ...prev,
        current:       data.current       ?? prev.current,
        total:         data.total         || prev.total,
        lastSceneMs:   data.lastSceneMs   ?? prev.lastSceneMs,
        lastSceneName: data.lastSceneName ?? prev.lastSceneName,
      }));
    });

    socket.on('layoutAgentTrace', (data) => {
      if (data.sessionId !== sessionId || !data.run) return;
      const run = data.run;
      const sceneIds = Array.isArray(run.sceneIds) ? run.sceneIds : [];
      if (sceneIds.length === 0) return;

      setLiveAgentRunsByScene(prev => {
        const next = { ...prev };
        for (const sid of sceneIds) {
          const existing = next[sid] || [];
          const runId = String(run.runId || `spread-${run.spreadIndex || 'unknown'}`);
          if (existing.some(r => String(r.runId || '') === runId)) continue;
          next[sid] = [...existing, run];
        }
        return next;
      });
    });

    const handleFinish = (data, cancelled) => {
      if (data.sessionId !== sessionId) return;
      if (data.layouts?.length) {
        setLayouts(prev => {
          const merged = [...prev];
          for (const nl of data.layouts) {
            const idx = merged.findIndex(l => l.sceneId === nl.sceneId);
            if (idx >= 0) merged[idx] = nl; else {
              // Insert new layout at the correct story-order position
              const storyScenes = story?.scenes || [];
              const nlOrder = storyScenes.findIndex(s => s.sceneId === nl.sceneId);
              let insertIdx = merged.length;
              if (nlOrder >= 0) {
                insertIdx = merged.findIndex(existing => {
                  const existOrder = storyScenes.findIndex(s => s.sceneId === existing.sceneId);
                  return existOrder < 0 || existOrder > nlOrder;
                });
                if (insertIdx < 0) insertIdx = merged.length;
              }
              merged.splice(insertIdx, 0, nl);
            }
          }
          return merged;
        });
        if (!cancelled) {
          setSelectedSceneIds(new Set());
          setSelectedSceneId(data.layouts[0]?.sceneId ?? null);
        }
      }
      if (data.tokenStats)   setTokenStats(data.tokenStats);
      if (data.debugFileName) setDebugFileName(data.debugFileName);
      if (data.failedScenes?.length) setFailedScenes(data.failedScenes);
      setGenerating(false);
      setCancelling(false);
      setGenProgress(prev => ({ ...prev, done: true }));
      if (!cancelled) {
        const layoutCount = data.layouts?.length ?? 0;
        showSnackbar(`✅ Layout generation complete — ${layoutCount} scene layout(s) ready.`, 'success');
      }
    };

    socket.on('layoutComplete',  (data) => handleFinish(data, false));
    socket.on('layoutCancelled', (data) => handleFinish(data, true));
    socket.on('layoutQuotaError', (data) => {
      if (data.sessionId !== sessionId) return;
      setFailedScenes(data.failedScenes || []);
    });
    socket.on('layoutError', (data) => {
      if (data.sessionId !== sessionId) return;
      setGenerating(false);
      setCancelling(false);
      setGenError(data.error || 'Layout generation failed');
    });
    socket.on('disconnect', () => console.log('[Step6] Layout socket disconnected'));

    socketRef.current = socket;
    return () => { socket.disconnect(); socketRef.current = null; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Seed progress total and start polling fallback when generating starts.
  // Polling ensures the bar updates even if WebSocket events are missed (proxy issues, reconnects, etc.).
  useEffect(() => {
    if (!generating) {
      // Clear polling interval when generation stops
      if (genProgressRef.current) {
        clearInterval(genProgressRef.current);
        genProgressRef.current = null;
      }
      return;
    }
    // Seed total from known scene count (replaced by real spread total once first event arrives)
    setGenProgress(prev => {
      const seedTotal = (selectedSceneIds && selectedSceneIds.size > 0)
        ? selectedSceneIds.size
        : includedScenes.length;
      return {
        ...prev,
        current: prev.done ? 0 : prev.current,
        total: prev.done ? seedTotal : (prev.total > 0 ? prev.total : seedTotal),
        done: false,
        startedAt: prev.done ? Date.now() : (prev.startedAt || Date.now()),
      };
    });
    // Start polling as a fallback to socket events (every 3 s)
    if (genProgressRef.current) clearInterval(genProgressRef.current);
    genProgressRef.current = setInterval(async () => {
      try {
        const data = await albumService.getLayoutProgress(sessionId);
        const prog = data?.progress;
        if (!prog) return;
        setGenProgress(prev => ({
          ...prev,
          current:       prog.current       ?? prev.current,
          total:         prog.total         || prev.total,
          lastSceneMs:   prog.lastSceneMs   ?? prev.lastSceneMs,
          lastSceneName: prog.lastSceneName ?? prev.lastSceneName,
        }));
        // Stop polling when done (require total > 0 to avoid stopping on a not-yet-initialised entry).
        // Also call setGenerating(false) as a fallback in case the socket layoutComplete event was missed.
        if (prog.done && prog.total > 0) {
          clearInterval(genProgressRef.current);
          genProgressRef.current = null;
          setGenerating(false);
        }
      } catch {
        // Silently ignore polling errors — socket events are primary
      }
    }, 3000);
    return () => {
      if (genProgressRef.current) { clearInterval(genProgressRef.current); genProgressRef.current = null; }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generating]);

  // ── Generate ────────────────────────────────────────────────────────────────
  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    setGenError(null);
    setFailedScenes([]);
    setLiveAgentRunsByScene({});
    // Reset progress so the bar starts fresh for this run
    setGenProgress({ current: 0, total: 0, lastSceneMs: null, lastSceneName: '', startedAt: Date.now(), done: false });
    try {
      const sceneIdsToSend = selectedSceneIds && selectedSceneIds.size > 0 ? [...selectedSceneIds] : undefined;
      // Fire-and-forget: backend responds immediately (202), then generates in background.
      // Layouts, tokenStats and debugFileName are delivered via socket 'layoutComplete' event.
      await albumService.generateAlbumLayout(sessionId, {
        debugSceneLimit: sceneLimitEnabled ? (debugSceneLimit || 0) : 0,
        pageMarginMm: pageMarginMm || 0,
        sceneIds: sceneIdsToSend,
        includeAllQuality,
        albumTemplateId: selectedAlbumTemplateId || undefined,
        userContext: albumContext,
        allowCrossSceneImages,
        scenesPerIteration,
        showTitleStrip,
        budgetConfig,
        aiModel,
        temperature: layoutTemperature,
        useLayoutAgent,
      });
      // Response arrived quickly — generation is running in background.
      // Progress updates and final layouts come through the socket.
    } catch (err) {
      if (err.response?.status === 409) {
        setRunningJobInfo(err.response.data?.progress || {});
        setAlreadyRunningOpen(true);
        setGenerating(false);
      } else {
        setGenError(err.response?.data?.error || err.message || 'Failed to start layout generation');
        setGenerating(false);
      }
    }
    // Note: setGenerating(false) is intentionally NOT in a finally block here.
    // The socket 'layoutComplete' / 'layoutCancelled' / 'layoutError' events handle that.
  }, [sessionId, debugSceneLimit, sceneLimitEnabled, selectedSceneIds, includeAllQuality, selectedAlbumTemplateId, pageMarginMm, albumContext, allowCrossSceneImages, scenesPerIteration, showTitleStrip, budgetConfig, aiModel, layoutTemperature, useLayoutAgent]);
  // ── Cancel ongoing layout generation ────────────────────────────────────
  const handleCancel = useCallback(async () => {
    setCancelling(true);
    try {
      await albumService.cancelAlbumLayout(sessionId);
    } catch (err) {
      console.error('[Step6] Cancel request failed:', err);
      setCancelling(false);
    }
  }, [sessionId]);
  // ── Generate plan ────────────────────────────────────────────────────────────
  const handleGeneratePlan = useCallback(async () => {
    setPlanGenerating(true);
    setPlanError(null);
    try {
      const data = await albumService.generateAlbumPlan(sessionId, {
        aiModel,
        temperature: planTemperature,
        peopleVsScenery,
        usePlanAgent,
      });
      if (data?.albumPlan) {
        setAlbumPlan(data.albumPlan);
        if (data.albumPlan.debugFileName) setPlanDebugFileName(data.albumPlan.debugFileName);
        // Auto-select all plan scenes (user can deselect as needed)
        if (data.albumPlan.scene_plan?.length) {
          setSelectedSceneIds(new Set(data.albumPlan.scene_plan.map(e => e.sceneId)));
        }
        const sceneCount = data.albumPlan.scene_plan?.length ?? 0;
        showSnackbar(`✅ Album plan complete — ${sceneCount} scene(s) planned.`, 'success');
      }
    } catch (err) {
      setPlanError(err.response?.data?.error || err.message || 'Plan generation failed');
    } finally {
      setPlanGenerating(false);
    }
  }, [sessionId, aiModel, planTemperature, peopleVsScenery, usePlanAgent]);

  // ── Save edited JSON ────────────────────────────────────────────────────────
  const handleSaveJson = useCallback(() => {
    try {
      const parsed = JSON.parse(editedJson);
      setJsonError(null);
      setLayouts(prev => prev.map(l => l.sceneId === selectedSceneId ? parsed : l));
    } catch (e) {
      setJsonError(e.message);
    }
  }, [editedJson, selectedSceneId]);

  // ── Persist all layouts ─────────────────────────────────────────────────────
  const handleSaveAll = useCallback(async () => {
    setSaving(true);
    try {
      await albumService.saveAlbumLayout(sessionId, layouts);
      setSaveMsg('Saved');
      setTimeout(() => setSaveMsg(''), 2500);
    } catch (err) {
      setSaveMsg('Save failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  }, [sessionId, layouts]);

  // ── Delete layouts for selected scenes ────────────────────────────────────
  const handleDeleteLayouts = useCallback(async () => {
    const existingIds = new Set(layouts.map(l => l.sceneId));
    const idsToDelete = selectedSceneIds && selectedSceneIds.size > 0
      ? [...selectedSceneIds].filter(id => existingIds.has(id))
      : layouts.map(l => l.sceneId);
    try {
      await albumService.deleteAlbumLayouts(sessionId, idsToDelete);
      setLayouts(prev => prev.filter(l => !idsToDelete.includes(l.sceneId)));
      // Re-add deleted scene IDs to selection so they show as ungenerated
      setSelectedSceneIds(prev => {
        const next = new Set(prev || []);
        idsToDelete.forEach(id => next.add(id));
        return next;
      });
      if (idsToDelete.includes(selectedSceneId)) setSelectedSceneId(null);
    } catch (err) {
      console.error('Delete layouts failed:', err);
    }
  }, [sessionId, selectedSceneIds, layouts, selectedSceneId]);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const includedScenes = useMemo(
    () => (story?.scenes || []).filter(s => s.includedInAlbum !== false),
    [story]
  );
  const layoutById = useMemo(() => new Map(layouts.map(l => [l.sceneId, l])), [layouts]);
  const scoreColor = (s) => s >= 8 ? 'success' : s >= 5 ? 'warning' : 'error';

  // When a plan exists use its ordered scene list (enriched with story fields + planBudget/planRole);
  // otherwise fall back to all included scenes.
  const selectableScenes = useMemo(() => {
    if (albumPlan?.scene_plan?.length) {
      return albumPlan.scene_plan.map(entry => {
        const scene = (story?.scenes || []).find(s => s.sceneId === entry.sceneId);
        return {
          ...(scene || {}),
          sceneId:   entry.sceneId,
          sceneName: entry.sceneName || entry.combined_scene_name || scene?.sceneName || entry.sceneId,
          planBudget: entry.budget,
          planRole:   entry.role,
          mergeWith:  entry.merge_with,
        };
      });
    }
    return includedScenes;
  }, [albumPlan, includedScenes, story]);

  const sceneInputMapRows = useMemo(() => {
    if (!albumPlan) return [];

    const storyScenes = story?.scenes || [];
    const storyById = new Map(storyScenes.map((s, idx) => [s.sceneId, { scene: s, idx }]));
    const planById = new Map((albumPlan.scene_plan || []).map((p) => [p.sceneId, p]));

    const rawRows = Array.isArray(albumPlan.scene_input_map) && albumPlan.scene_input_map.length > 0
      ? albumPlan.scene_input_map
      : (albumPlan.scene_plan || []).flatMap((entry) => {
          const sourceIds = Array.isArray(entry.source_scene_ids) ? entry.source_scene_ids : [];
          return sourceIds.map((sid) => ({ input_scene_id: sid, plan_entry_id: entry.sceneId }));
        });

    return rawRows
      .map((r) => {
        const inputId = String(r?.input_scene_id || '').trim();
        const planEntryId = String(r?.plan_entry_id || '').trim();
        if (!inputId || !planEntryId) return null;
        const inputMeta = storyById.get(inputId);
        const planEntry = planById.get(planEntryId);
        return {
          inputSceneId: inputId,
          inputSceneName: inputMeta?.scene?.sceneName || inputId,
          planEntryId,
          planEntryName: planEntry?.sceneName || planEntry?.combined_scene_name || planEntryId,
          merged: inputId !== planEntryId,
          sortOrder: Number.isFinite(inputMeta?.idx) ? inputMeta.idx : Number.MAX_SAFE_INTEGER,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.inputSceneId.localeCompare(b.inputSceneId));
  }, [albumPlan, story]);

  const rawStep5SceneRows = useMemo(() => {
    const storyScenes = story?.scenes || [];
    if (!storyScenes.length) return [];

    const planById = new Map((albumPlan?.scene_plan || []).map((p) => [p.sceneId, p]));
    const mapByInputId = new Map(sceneInputMapRows.map((r) => [r.inputSceneId, r]));

    return storyScenes.map((scene, idx) => {
      const mapped = mapByInputId.get(scene.sceneId);
      const planEntry = mapped ? planById.get(mapped.planEntryId) : null;
      return {
        idx,
        sceneId: scene.sceneId,
        sceneName: scene.sceneName || scene.sceneId,
        emoji: scene.emoji || '📷',
        dateHint: scene.dateHint || '',
        themeRelevanceScore: scene.themeRelevanceScore,
        mappedPlanEntryId: mapped?.planEntryId || '',
        mappedPlanEntryName: mapped?.planEntryName || '',
        mappedType: mapped ? (mapped.merged ? 'merged' : 'direct') : 'unmapped',
        mappedPlanBudget: planEntry?.budget,
      };
    });
  }, [story, albumPlan, sceneInputMapRows]);

  // ────────────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress size={36} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading layout data…</Typography>
      </Box>
    );
  }

  if (loadError) {
    return <Alert severity="error" sx={{ m: 2 }}>{loadError}</Alert>;
  }

  if (!story) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        No story timeline found. Please complete Step 5 (Story / Timeline) first.
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 4 }}>
      {/* ── Tab bar ──────────────────────────────────────────────────────────────── */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={(_e, v) => setActiveTab(v)} aria-label="Step 6 tabs">
          <Tab
            label="Plan"
            icon={<PlanIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            sx={{ minHeight: 40, textTransform: 'none' }}
          />
          <Tab
            label="Layout"
            icon={<PagesIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            sx={{ minHeight: 40, textTransform: 'none' }}
          />
        </Tabs>
      </Box>

      {/* ── Plan Tab ──────────────────────────────────────────────────────────────── */}
      {activeTab === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Instructions */}
          <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50', borderLeft: '3px solid', borderColor: 'secondary.main', borderRadius: '0 8px 8px 0' }}>
            <Typography variant="body2" color="text.secondary">
              Generate a <strong>planning summary</strong> before layout. The AI sees all scenes at once
              and decides how many album pages (budget 1–4) each scene deserves, assigns a narrative
              role, and can reorder scenes for the best story arc. The layout step will then follow this plan.
            </Typography>
          </Box>

          {/* Actions */}
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>AI Model</InputLabel>
              <Select
                value={aiModel}
                label="AI Model"
                onChange={e => setAiModel(e.target.value)}
              >
                {availableModels.length > 0
                  ? availableModels.map(m => (
                      <MenuItem key={m.id} value={m.id} disabled={!m.available}>
                        {m.label} ({m.provider}){!m.available ? ' — not configured' : ''}
                      </MenuItem>
                    ))
                  : [
                      <MenuItem key="gpt-6.1-sol" value="gpt-6.1-sol">GPT-6.1 Sol (openai)</MenuItem>,
                      <MenuItem key="claude-opus-4-6" value="claude-opus-4-6">Claude Opus 4.6 (anthropic)</MenuItem>,
                    ]
                }
              </Select>
            </FormControl>
            <Tooltip title="Plan temperature: controls creativity of scene selection and ordering. Default: 0.4" placement="top" arrow>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 160 }}>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>Plan temp:</Typography>
                <Select
                  size="small"
                  value={planTemperature}
                  onChange={e => setPlanTemperature(Number(e.target.value))}
                  sx={{ fontSize: 13, minWidth: 70 }}
                  variant="standard"
                >
                  {[0.0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0].map(t => (
                    <MenuItem key={t} value={t}>{t.toFixed(1)}</MenuItem>
                  ))}
                </Select>
              </Stack>
            </Tooltip>

            {/* ── People vs Scenery balance ─────────────────────────────── */}
            <Tooltip
              title="Controls how much the plan favours images of key people vs. scenery/landmarks. 1 = all scenery, 10 = all people. Default: 5 (balanced)."
              placement="top"
              arrow
            >
              <Stack spacing={0.5} sx={{ minWidth: 260 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    People vs. Scenery focus
                  </Typography>
                  <Typography variant="caption" fontWeight={600} color="primary">
                    {peopleVsScenery === 1 ? '1 – All scenery' :
                     peopleVsScenery === 2 ? '2 – Mostly scenery' :
                     peopleVsScenery === 3 ? '3 – Scenery-leaning' :
                     peopleVsScenery === 4 ? '4 – Slight scenery lean' :
                     peopleVsScenery === 5 ? '5 – Balanced' :
                     peopleVsScenery === 6 ? '6 – Slight people lean' :
                     peopleVsScenery === 7 ? '7 – People-leaning' :
                     peopleVsScenery === 8 ? '8 – Mostly people' :
                     peopleVsScenery === 9 ? '9 – Strong people focus' :
                     '10 – All people'}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>🏛️ Scenery</Typography>
                  <Slider
                    value={peopleVsScenery}
                    onChange={(_, v) => setPeopleVsScenery(v)}
                    min={1}
                    max={10}
                    step={1}
                    marks
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>👤 People</Typography>
                </Stack>
              </Stack>
            </Tooltip>

            <Tooltip
              title="Enable plan agent loop: runs stricter spread/rating validations and retries with targeted fix instructions when the plan violates rules."
              placement="top"
              arrow
            >
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={usePlanAgent}
                    onChange={e => setUsePlanAgent(e.target.checked)}
                    color="secondary"
                  />
                }
                label={
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <AgentIcon sx={{ fontSize: 15, color: usePlanAgent ? 'secondary.main' : 'text.disabled' }} />
                    <Typography variant="body2" color={usePlanAgent ? 'secondary.main' : 'text.secondary'} fontWeight={usePlanAgent ? 600 : 400}>
                      Plan agent loop
                    </Typography>
                    {usePlanAgent && (
                      <Chip label="beta" size="small" color="secondary" variant="outlined" sx={{ height: 14, fontSize: 9, px: 0.25 }} />
                    )}
                  </Stack>
                }
                sx={{ m: 0 }}
              />
            </Tooltip>

            <Button
              variant="contained"
              color="secondary"
              startIcon={planGenerating ? <CircularProgress size={18} color="inherit" /> : <PlanIcon />}
              onClick={handleGeneratePlan}
              disabled={planGenerating || !story}
              size="large"
            >
              {planGenerating
                ? `Generating plan\u2026 ${planElapsedSec > 0 ? `(${planElapsedSec}s)` : ''}`
                : albumPlan ? 'Regenerate plan' : 'Generate plan'}
            </Button>
            {albumPlan?.generatedAt && (
              <Chip
                label={`Generated ${new Date(albumPlan.generatedAt).toLocaleString('en-GB')}`}
                size="small"
                icon={<CheckIcon />}
                color="success"
                variant="outlined"
              />
            )}
            {albumPlan?.tokenStats && (
              <>
                <Chip label={albumPlan.tokenStats.model} size="small" />
                <Chip
                  label={`$${albumPlan.tokenStats.costUsd?.toFixed(4) ?? '—'}`}
                  size="small"
                  color="secondary"
                  title="Plan generation cost"
                />
                <Chip
                  label={`${(albumPlan.tokenStats.totalTokens || 0).toLocaleString()} tokens`}
                  size="small"
                  color="info"
                />
                {albumPlan.tokenStats.durationMs > 0 && (
                  <Chip
                    label={`${(albumPlan.tokenStats.durationMs / 1000).toFixed(1)}s`}
                    size="small"
                    color="info"
                    title="Plan generation duration"
                  />
                )}
              </>
            )}
            {(planDebugFileName || albumPlan?.debugFileName) && (
              <Chip
                icon={<JsonIcon />}
                label={planDebugFileName || albumPlan.debugFileName}
                size="small"
                variant="outlined"
                sx={{ fontFamily: 'monospace', fontSize: 11 }}
                title="Plan debug log written at workspace root"
              />
            )}
          </Stack>

          {planError && <Alert severity="error" onClose={() => setPlanError(null)}>{planError}</Alert>}

          {albumPlan?.planAgentDebug && (
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Plan Agent Trace
                </Typography>
                <Chip
                  size="small"
                  color={albumPlan.planAgentDebug.accepted ? 'success' : 'warning'}
                  label={albumPlan.planAgentDebug.accepted ? 'accepted' : 'not fully accepted'}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`submit attempts ${albumPlan.planAgentDebug.submitAttempts}/${albumPlan.planAgentDebug.maxSubmitAttempts}`}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`final validation errors ${albumPlan.planAgentDebug.validationErrorCount || 0}`}
                />
                {albumPlan.planAgentDebug.maxAttemptsReached && (
                  <Chip size="small" color="error" label="max attempts reached" />
                )}
                <Tooltip title={showPlanAgentTranscript ? 'Hide transcript' : 'Show transcript'}>
                  <IconButton
                    size="small"
                    onClick={() => setShowPlanAgentTranscript(v => !v)}
                  >
                    {showPlanAgentTranscript ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Stack>

              {Array.isArray(albumPlan.planAgentDebug.submitEvents) && albumPlan.planAgentDebug.submitEvents.length > 0 && (
                <Stack spacing={1} sx={{ mt: 1.25 }}>
                  {albumPlan.planAgentDebug.submitEvents.map((evt, idx) => (
                    <Box
                      key={`submit-event-${idx}`}
                      sx={{
                        border: '1px solid',
                        borderColor: evt.accepted ? 'success.light' : 'warning.light',
                        borderRadius: 1,
                        p: 1,
                        bgcolor: evt.accepted ? 'success.50' : 'warning.50',
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={`submit #${evt.attempt}`} />
                        <Chip
                          size="small"
                          color={evt.accepted ? 'success' : 'warning'}
                          label={evt.accepted ? 'accepted' : 'rejected'}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {evt.errorCount || 0} error(s)
                        </Typography>
                      </Stack>
                      {!evt.accepted && Array.isArray(evt.errors) && evt.errors.length > 0 && (
                        <Box sx={{ mt: 0.75 }}>
                          {evt.errors.slice(0, 5).map((err, eIdx) => (
                            <Typography key={`evt-${idx}-err-${eIdx}`} variant="caption" display="block" color="text.secondary">
                              {err}
                            </Typography>
                          ))}
                          {evt.errors.length > 5 && (
                            <Typography variant="caption" color="text.disabled">
                              +{evt.errors.length - 5} more
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Stack>
              )}

              <Collapse in={showPlanAgentTranscript}>
                <Box
                  sx={{
                    mt: 1.25,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: '#10151f',
                    color: '#d7e0ea',
                    fontFamily: 'monospace',
                    fontSize: 11,
                    whiteSpace: 'pre-wrap',
                    maxHeight: 280,
                    overflow: 'auto',
                  }}
                >
                  {(albumPlan.planAgentDebug.transcript || []).join('\n') || 'No transcript available.'}
                </Box>
              </Collapse>
            </Paper>
          )}

          {sceneInputMapRows.length > 0 && (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Scene Mapping
                </Typography>
                <Chip size="small" variant="outlined" label={`${sceneInputMapRows.length} mapped scene(s)`} />
              </Stack>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Input scene (Step 5)</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Mapped to plan entry (Step 6)</TableCell>
                    <TableCell sx={{ width: 100, fontWeight: 600, fontSize: 12 }}>Type</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sceneInputMapRows.map((row) => (
                    <TableRow key={`${row.inputSceneId}->${row.planEntryId}`} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.inputSceneName}</Typography>
                        <Typography variant="caption" color="text.secondary">{row.inputSceneId}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.planEntryName}</Typography>
                        <Typography variant="caption" color="text.secondary">{row.planEntryId}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={row.merged ? 'warning' : 'success'}
                          label={row.merged ? 'merged' : 'direct'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}

          {/* Plan table */}
          {albumPlan?.scene_plan?.length > 0 ? (
            <>
              {/* Spread Map */}
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <SpreadMap
                  scenePlan={albumPlan.scene_plan}
                  storyScenes={story?.scenes}
                  highlightSceneId={planHighlightSceneId}
                  onSceneClick={(sceneId) => {
                    setPlanHighlightSceneId(sceneId);
                    // Scroll the table row into view
                    document
                      .getElementById(`plan-row-${sceneId}`)
                      ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                  }}
                />
              </Paper>

              <Paper variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <Tooltip title="Proposed album order — 1 = first scene. Arrows auto-save to DB." placement="top" arrow>
                        <TableCell sx={{ width: 32, fontWeight: 600, fontSize: 12, cursor: 'help' }}>#</TableCell>
                      </Tooltip>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Scene</TableCell>
                      <Tooltip title="Pages allocated to this scene: 1p = 1 page, 2p = full spread, 3p = spread+1, 4p = 2 spreads. Select to override." placement="top" arrow>
                        <TableCell sx={{ width: 90, fontWeight: 600, fontSize: 12, cursor: 'help' }}>Budget</TableCell>
                      </Tooltip>
                      <Tooltip title="Narrative role in the album arc" placement="top" arrow>
                        <TableCell sx={{ width: 100, fontWeight: 600, fontSize: 12, cursor: 'help' }}>Role</TableCell>
                      </Tooltip>
                      <Tooltip title="Recommended layout density for the layout phase" placement="top" arrow>
                        <TableCell sx={{ width: 105, fontWeight: 600, fontSize: 12, cursor: 'help' }}>Layout style</TableCell>
                      </Tooltip>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Reasoning</TableCell>
                      <Tooltip title="Drag scenes or use arrows to reorder. Changes auto-save." placement="top" arrow>
                        <TableCell sx={{ width: 70, fontWeight: 600, fontSize: 12, cursor: 'help' }}>Order ↕</TableCell>
                      </Tooltip>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {albumPlan.scene_plan.map((entry, idx) => {
                      const scene = story?.scenes?.find(s => s.sceneId === entry.sceneId);
                      const isExpanded = planRowExpanded[entry.sceneId];
                      const role = (entry.role || 'transition').toLowerCase();
                      const roleColor = { opening: 'secondary', buildup: 'primary', climax: 'error', transition: 'default', close: 'success' }[role] || 'default';
                      const budgetColor = { 1: 'default', 2: 'info', 3: 'warning', 4: 'error' }[entry.budget] || 'default';
                      const wasReordered = entry.originalIndex !== undefined && entry.originalIndex !== idx;
                      const style = (entry.layout_style || 'balanced').toLowerCase();
                      const styleMeta = LAYOUT_STYLE_META[style] || LAYOUT_STYLE_META.balanced;
                      const isHighlighted = entry.sceneId === planHighlightSceneId;
                      return (
                        <TableRow
                          id={`plan-row-${entry.sceneId}`}
                          key={entry.sceneId}
                          hover
                          onClick={() => setPlanHighlightSceneId(entry.sceneId)}
                          sx={{ bgcolor: isHighlighted ? 'primary.50' : undefined, cursor: 'default' }}
                        >
                          <TableCell sx={{ color: 'text.disabled', fontSize: 12, py: 0.75 }}>{idx + 1}</TableCell>
                          <TableCell sx={{ py: 0.75 }}>
                            <Stack direction="column" spacing={0.25}>
                              <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" useFlexGap>
                                <Typography variant="body2" sx={{ fontWeight: wasReordered ? 600 : 400 }}>
                                  {scene?.emoji} {entry.sceneName || entry.combined_scene_name || scene?.sceneName || entry.sceneId}
                                </Typography>
                                {entry.pages && (
                                  <Chip label={entry.pages} size="small" variant="outlined" sx={{ height: 18, fontSize: 10, color: 'text.secondary' }} />
                                )}
                                {wasReordered && (
                                  <Chip label={`was #${entry.originalIndex + 1}`} size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                                )}
                              </Stack>
                              {Array.isArray(entry.merge_with) && entry.merge_with.length > 0 && (() => {
                                const mergedNames = entry.merge_with.map(mid => {
                                  const ms = story?.scenes?.find(s => s.sceneId === mid);
                                  return ms ? `${ms.emoji || '📷'} ${ms.sceneName}` : mid;
                                });
                                return (
                                  <Typography variant="caption" color="info.main" sx={{ fontSize: 10, lineHeight: 1.3 }}>
                                    Merged with: {mergedNames.join(' · ')}
                                  </Typography>
                                );
                              })()}
                              {entry.description && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, lineHeight: 1.3 }}>
                                  {entry.description}
                                </Typography>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ py: 0.75 }}>
                            <FormControl size="small">
                              <Select
                                value={entry.budget}
                                variant="standard"
                                onChange={async (e) => {
                                  const newBudget = Number(e.target.value);
                                  const updatedPlan = albumPlan.scene_plan.map((p, i) =>
                                    i === idx ? { ...p, budget: newBudget } : p
                                  );
                                  setAlbumPlan(prev => ({ ...prev, scene_plan: updatedPlan }));
                                  await albumService.updateAlbumPlan(sessionId, { scene_plan: updatedPlan })
                                    .catch(err => console.error('Plan save failed:', err));
                                }}
                                sx={{ fontSize: 13 }}
                                renderValue={(v) => (
                                  <Chip
                                    label={`${v}p`}
                                    size="small"
                                    color={budgetColor}
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: 11, cursor: 'pointer', pointerEvents: 'none' }}
                                  />
                                )}
                              >
                                {[1, 2, 3, 4].map(b => (
                                  <MenuItem key={b} value={b}>{b} page{b > 1 ? 's' : ''}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell sx={{ py: 0.75 }}>
                            <Chip
                              label={role}
                              size="small"
                              color={roleColor}
                              variant="filled"
                              sx={{ height: 20, fontSize: 11, textTransform: 'capitalize' }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 0.75 }}>
                            <Tooltip title={`${styleMeta.desc}${entry.layout_style_reason ? ` — ${entry.layout_style_reason}` : ''}`} placement="top" arrow>
                              <Chip
                                label={`${styleMeta.icon} ${styleMeta.label}`}
                                size="small"
                                variant="outlined"
                                sx={{
                                  height: 20, fontSize: 11,
                                  borderColor: styleMeta.color,
                                  color: styleMeta.color,
                                  cursor: 'help',
                                }}
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ py: 0.75 }}>
                            {entry.reasoning ? (
                              <>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: isExpanded ? 'unset' : 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: isExpanded ? 'visible' : 'hidden',
                                    lineHeight: 1.4,
                                    cursor: 'pointer',
                                  }}
                                  onClick={(e) => { e.stopPropagation(); setPlanRowExpanded(prev => ({ ...prev, [entry.sceneId]: !prev[entry.sceneId] })); }}
                                >
                                  {entry.reasoning}
                                </Typography>
                                {entry.reasoning.length > 120 && (
                                  <Typography
                                    variant="caption"
                                    color="primary.main"
                                    sx={{ cursor: 'pointer', display: 'block', mt: 0.25 }}
                                    onClick={(e) => { e.stopPropagation(); setPlanRowExpanded(prev => ({ ...prev, [entry.sceneId]: !prev[entry.sceneId] })); }}
                                  >
                                    {isExpanded ? 'Less ▲' : 'More ▼'}
                                  </Typography>
                                )}
                              </>
                            ) : (
                              <Typography variant="caption" color="text.disabled">\u2014</Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ py: 0.75 }}>
                            <Stack direction="row" spacing={0}>
                              <Tooltip title="Move up (auto-saves)">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={idx === 0}
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      const updated = [...albumPlan.scene_plan];
                                      [updated[idx - 1], updated[idx]] = [updated[idx], updated[idx - 1]];
                                      setAlbumPlan(prev => ({ ...prev, scene_plan: updated }));
                                      await albumService.updateAlbumPlan(sessionId, { scene_plan: updated })
                                        .catch(err => console.error('Plan save failed:', err));
                                    }}
                                  >
                                    <NavigateBefore sx={{ transform: 'rotate(90deg)', fontSize: 18 }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Move down (auto-saves)">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={idx === albumPlan.scene_plan.length - 1}
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      const updated = [...albumPlan.scene_plan];
                                      [updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]];
                                      setAlbumPlan(prev => ({ ...prev, scene_plan: updated }));
                                      await albumService.updateAlbumPlan(sessionId, { scene_plan: updated })
                                        .catch(err => console.error('Plan save failed:', err));
                                    }}
                                  >
                                    <NavigateNext sx={{ transform: 'rotate(90deg)', fontSize: 18 }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>

              {/* Plan footer — arc notes */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  {albumPlan.scene_plan?.length > 0 && (() => {
                    const totalPages = albumPlan.scene_plan.reduce((sum, e) => sum + Math.max(1, e.budget || 1), 0);
                    const totalSpreads = Math.ceil((totalPages + 1) / 2); // +1 for blank cover page
                    return (
                      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Album size:</Typography>
                        <Chip label={`${totalPages} pages`} size="small" color="info" />
                        <Chip label={`${totalSpreads} spreads`} size="small" variant="outlined" />
                      </Stack>
                    );
                  })()}
                  {albumPlan.album_arc_notes && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                        NARRATIVE ARC
                      </Typography>
                      <Typography variant="body2">{albumPlan.album_arc_notes}</Typography>
                    </Box>
                  )}
                  {albumPlan.reorder_notes && albumPlan.reorder_notes !== 'No reorders \u2014 original order preserved' && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                        SCENE REORDER
                      </Typography>
                      <Typography variant="body2">{albumPlan.reorder_notes}</Typography>
                    </Box>
                  )}
                </Stack>
              </Paper>

              {/* Guidance: move to Layout tab */}
              <Alert
                severity="success"
                icon={<GoLayoutIcon />}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    endIcon={<GoLayoutIcon />}
                    onClick={() => setActiveTab(1)}
                    sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
                  >
                    Go to Layout tab
                  </Button>
                }
              >
                <strong>Plan approved?</strong> Switch to the <strong>Layout</strong> tab to run AI layout generation
                using this plan. The layout engine will follow the budgets and layout-style guidance from above.
              </Alert>
            </>
          ) : (
            !planGenerating && (
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                <PlanIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
                <Typography variant="body1" sx={{ mb: 1 }}>No plan generated yet</Typography>
                <Typography variant="body2">
                  Click <strong>Generate plan</strong> above to let the AI decide page budgets,
                  narrative roles, and optimal scene order for your album.
                </Typography>
              </Paper>
            )
          )}
        </Box>
      )}

      {/* ── Layout Tab ────────────────────────────────────────────────────────────── */}
      {activeTab === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* ── Top navigation ──────────────────────────────────────────────────────── */}
      {/* ── Instructions ── */}
      <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50', borderLeft: '3px solid', borderColor: 'primary.main', borderRadius: '0 8px 8px 0' }}>
        <Typography variant="body2" color="text.secondary">
          AI designs an A4 landscape page layout for each story scene — placing images with artistic
          compositions, varying sizes, and zoom focusing on key faces. Select scenes and click
          <strong> Generate</strong>.
        </Typography>
      </Box>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ flex: 1 }}>
          AI Page Layout Design
        </Typography>
        {layouts.length > 0 && (
          <Chip label={`${layouts.length} scenes designed`} color="success" size="small" icon={<CheckIcon />} />
        )}
        {includedScenes.length > 0 && (
          <Chip label={`${includedScenes.length} included scenes`} size="small" />
        )}
      </Box>

      {/* Configuration section header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5, lineHeight: 1, whiteSpace: 'nowrap' }}>Configuration</Typography>
        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
      </Box>

      {/* Settings + Generate row */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <Stack direction="column" spacing={0.5}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={sceneLimitEnabled}
                  onChange={e => setSceneLimitEnabled(e.target.checked)}
                />
              }
              label={<Typography variant="body2">Limit scenes (debug)</Typography>}
              sx={{ m: 0 }}
            />
            {sceneLimitEnabled && (
              <TextField
                label="Max scenes"
                type="number"
                value={debugSceneLimit}
                onChange={e => setDebugSceneLimit(Math.max(1, parseInt(e.target.value) || 1))}
                size="small"
                sx={{ width: 140 }}
                inputProps={{ min: 1, max: 999 }}
                helperText={`⚠ First ${debugSceneLimit} scene(s) only`}
                color="warning"
                focused
              />
            )}
          </Stack>
          <TextField
            label="Page margin (mm)"
            type="number"
            value={pageMarginMm}
            onChange={e => setPageMarginMm(Math.max(0, Math.min(30, parseFloat(e.target.value) || 0)))}
            size="small"
            sx={{ width: 160 }}
            inputProps={{ min: 0, max: 30, step: 1 }}
            helperText={pageMarginMm > 0 ? `${pageMarginMm}mm border, no bleed` : '0 = bleed to edges'}
          />

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Layout templates</InputLabel>
            <Select
              value={selectedAlbumTemplateId}
              label="Layout templates"
              onChange={e => setSelectedAlbumTemplateId(e.target.value)}
            >
              {albumTemplates.map(at => (
                <MenuItem key={at._id} value={at._id}>{at.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>AI Model</InputLabel>
            <Select
              value={aiModel}
              label="AI Model"
              onChange={e => setAiModel(e.target.value)}
            >
              {availableModels.length > 0
                ? availableModels.map(m => (
                    <MenuItem key={m.id} value={m.id} disabled={!m.available}>
                      {m.label} ({m.provider}){!m.available ? ' — not configured' : ''}
                    </MenuItem>
                  ))
                : [
                    <MenuItem key="gpt-6.1-sol" value="gpt-6.1-sol">GPT-6.1 Sol (openai)</MenuItem>,
                    <MenuItem key="claude-opus-4-6" value="claude-opus-4-6">Claude Opus 4.6 (anthropic)</MenuItem>,
                  ]
              }
            </Select>
          </FormControl>

          <Tooltip title="Layout temperature: controls creativity of page layout generation. Default: 0.7" placement="top" arrow>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>Layout temp:</Typography>
              <Select
                size="small"
                value={layoutTemperature}
                onChange={e => setLayoutTemperature(Number(e.target.value))}
                sx={{ fontSize: 13, minWidth: 70 }}
                variant="standard"
              >
                {[0.0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0].map(t => (
                  <MenuItem key={t} value={t}>{t.toFixed(1)}</MenuItem>
                ))}
              </Select>
            </Stack>
          </Tooltip>

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={includeAllQuality}
                onChange={e => setIncludeAllQuality(e.target.checked)}
                color="warning"
              />
            }
            label={<Typography variant="body2" color={includeAllQuality ? 'warning.main' : 'text.secondary'}>Include blurry / not&nbsp;album&#8209;worthy</Typography>}
            sx={{ m: 0, ml: 1 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={allowCrossSceneImages}
                onChange={e => setAllowCrossSceneImages(e.target.checked)}
              />
            }
            label={<Typography variant="body2">Allow support images from other scenes when needed</Typography>}
            sx={{ m: 0 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showTitleStrip}
                onChange={e => setShowTitleStrip(e.target.checked)}
              />
            }
            label={<Typography variant="body2">Include scene title inside album page</Typography>}
            sx={{ m: 0 }}
          />

          <Tooltip
            title="Enable the LangChain AI agent loop: validates each layout (image repetition, aspect ratio, template bank) and sends fix requests to the model (up to 3 retries). Also exposes tools to the model: get image details, available layouts, scene details, album context."
            placement="top"
            arrow
          >
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={useLayoutAgent}
                  onChange={e => setUseLayoutAgent(e.target.checked)}
                  color="secondary"
                />
              }
              label={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <AgentIcon sx={{ fontSize: 15, color: useLayoutAgent ? 'secondary.main' : 'text.disabled' }} />
                  <Typography variant="body2" color={useLayoutAgent ? 'secondary.main' : 'text.secondary'} fontWeight={useLayoutAgent ? 600 : 400}>
                    AI agent loop
                  </Typography>
                  {useLayoutAgent && (
                    <Chip label="beta" size="small" color="secondary" variant="outlined" sx={{ height: 14, fontSize: 9, px: 0.25 }} />
                  )}
                </Stack>
              }
              sx={{ m: 0 }}
            />
          </Tooltip>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="scenes-per-iter-label">Iteration mode</InputLabel>
            <Select
              labelId="scenes-per-iter-label"
              value={albumPlan ? 'plan' : scenesPerIteration}
              label="Iteration mode"
              onChange={e => { if (e.target.value !== 'plan') setScenesPerIteration(Number(e.target.value)); }}
              disabled={!!albumPlan}
            >
              {albumPlan
                ? <MenuItem value="plan">Spread-by-spread (plan-driven)</MenuItem>
                : [
                    <MenuItem key={1} value={1}>1 – solo scene per call</MenuItem>,
                    <MenuItem key={2} value={2}>2 – scene pair per call</MenuItem>,
                  ]
              }
            </Select>
          </FormControl>

          {/* Budget thresholds */}
          <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap" useFlexGap>
            <TextField
              label="2-page min score"
              type="number"
              value={budgetConfig.budget2MinScore}
              onChange={e => setBudgetConfig(c => ({ ...c, budget2MinScore: Math.max(1, Math.min(10, parseInt(e.target.value) || 9)) }))}
              size="small"
              sx={{ width: 140 }}
              inputProps={{ min: 1, max: 10 }}
              helperText="Score ≥ N → budget=2"
            />
            <TextField
              label="2-page min images"
              type="number"
              value={budgetConfig.budget2MinImages}
              onChange={e => setBudgetConfig(c => ({ ...c, budget2MinImages: Math.max(1, parseInt(e.target.value) || 5) }))}
              size="small"
              sx={{ width: 145 }}
              inputProps={{ min: 1, max: 50 }}
              helperText="Images ≥ N for 2-page"
            />
            <TextField
              label="3-page min images"
              type="number"
              value={budgetConfig.budget3MinImages}
              onChange={e => setBudgetConfig(c => ({ ...c, budget3MinImages: Math.max(1, parseInt(e.target.value) || 12) }))}
              size="small"
              sx={{ width: 145 }}
              inputProps={{ min: 1, max: 100 }}
              helperText="Images ≥ N for 3-page"
            />
          </Stack>

          <Box sx={{ flex: 1 }} />

          <Button
            variant="contained"
            size="large"
            startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <AIIcon />}
            onClick={() => {
              const regenCount = selectedSceneIds ? [...selectedSceneIds].filter(id => layoutById.has(id)).length : 0;
              regenCount > 0 ? setConfirmRegenOpen(true) : handleGenerate();
            }}
            disabled={generating || !story || (selectedSceneIds !== null && selectedSceneIds.size === 0)}
          >
            {generating
              ? 'Designing layouts\u2026'
              : `Generate layouts for ${selectedSceneIds?.size ?? includedScenes.length} scene(s)`}
          </Button>
          {generating && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={cancelling ? <CircularProgress size={16} color="inherit" /> : <CancelIcon />}
              onClick={handleCancel}
              disabled={cancelling}
              size="large"
            >
              {cancelling ? 'Cancelling…' : 'Cancel'}
            </Button>
          )}
          {layouts.length > 0 && (
            <>
              <Button
                variant="outlined"
                startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                onClick={handleSaveAll}
                disabled={saving}
                size="large"
              >
                {saveMsg || 'Save'}
              </Button>
              {(() => {
                const deleteCount = selectedSceneIds && selectedSceneIds.size > 0
                  ? [...selectedSceneIds].filter(id => layoutById.has(id)).length
                  : layouts.length;
                return deleteCount > 0 ? (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setConfirmDeleteOpen(true)}
                    disabled={generating}
                    size="large"
                  >
                    Delete {deleteCount} layout{deleteCount !== 1 ? 's' : ''}
                  </Button>
                ) : null;
              })()
              }
            </>
          )}
        </Stack>

        <TextField
          label="Add context for the album (optional)"
          placeholder="Extra guidance for layout: who to emphasize, moments, mood, what to avoid..."
          value={albumContext}
          onChange={e => setAlbumContext(e.target.value)}
          fullWidth
          multiline
          rows={2}
          size="small"
          sx={{ mt: 1.5 }}
          helperText={allowCrossSceneImages
            ? 'Used for this Step 6 generation/regeneration prompt. The model may also borrow a small number of fitting support images from other scenes.'
            : 'Used for this Step 6 generation/regeneration prompt.'}
        />

        {/* ── Progress block (visible while generating) ───────────────────── */}
        {generating && (() => {
          const total = genProgress.total || 0;
          const current = genProgress.current || 0;
          const pct = total > 0 ? Math.round((current / total) * 100) : 0;
          const elapsed = genProgress.startedAt ? Date.now() - genProgress.startedAt : 0;
          const avgMs = current > 0 ? elapsed / current : (genProgress.lastSceneMs || 20000);
          const remaining = Math.max(0, (total - current) * avgMs);
          const etaStr = total > 0 && current < total
            ? remaining > 60000
              ? `~${Math.floor(remaining / 60000)}m ${Math.ceil((remaining % 60000) / 1000)}s remaining`
              : `~${Math.ceil(remaining / 1000)}s remaining`
            : current >= total && total > 0 ? 'Wrapping up…' : 'Starting…';
          return (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <LinearProgress
                variant={total > 0 ? 'determinate' : 'indeterminate'}
                value={pct}
                sx={{ borderRadius: 1, height: 8, mb: 1 }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 90 }}>
                  {total > 0 ? `${current} / ${total} scenes` : 'Starting…'}
                </Typography>
                <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
                  {etaStr}
                </Typography>
                {genProgress.lastSceneMs > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Last: {(genProgress.lastSceneMs / 1000).toFixed(1)}s
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                  {pct}%
                </Typography>
              </Box>
              {genProgress.lastSceneName && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Last completed: “{genProgress.lastSceneName}”
                </Typography>
              )}
            </Box>
          );
        })()}

        {/* Token stats */}
        {tokenStats && (
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
            <Chip label={tokenStats.model} size="small" />
            {tokenStats.debugMode && (
              <Chip label={`DEBUG: ${tokenStats.debugSceneLimit} scenes`} size="small" color="warning" />
            )}
            {tokenStats.generatedAt && (
              <Chip label={new Date(tokenStats.generatedAt).toLocaleString('en-GB')} size="small" title="Generated at" />
            )}
            {tokenStats.durationMs && (
              <Chip label={`${(tokenStats.durationMs / 1000).toFixed(1)}s total`} size="small" color="info" title="Total response time (all scenes)" />
            )}
            {tokenStats.totalTokens && (
              <Chip label={`${tokenStats.totalTokens.toLocaleString()} tokens`} size="small" color="info" />
            )}
            {tokenStats.costUsd != null && (
              <Chip
                label={`$${tokenStats.costUsd.toFixed(4)}`}
                size="small"
                color="secondary"
                title={`Actual layout cost: ${tokenStats.promptTokens?.toLocaleString()} input + ${tokenStats.completionTokens?.toLocaleString()} output tokens @ $${tokenStats.priceInputPer1M}/$${tokenStats.priceOutputPer1M} per 1M`}
              />
            )}
            {tokenStats.cachedTokens > 0 && (
              <Chip
                label={`💾 ${tokenStats.cacheHitPct}% cached`}
                size="small"
                color="success"
                title={`${tokenStats.cachedTokens?.toLocaleString()} of ${tokenStats.promptTokens?.toLocaleString()} prompt tokens served from cache. Saved $${tokenStats.cacheSavingsUsd?.toFixed(4) ?? '0'} (without cache: $${tokenStats.costWithoutCacheUsd?.toFixed(4) ?? '?'})`}
              />
            )}
            <Chip label={`${tokenStats.successCount ?? layouts.length}/${tokenStats.sceneCount ?? layouts.length} scenes OK`} size="small" color={tokenStats.successCount === tokenStats.sceneCount ? 'success' : 'warning'} />
            {tokenStats.filteredImageCount > 0 && (
              <Chip
                label={`${tokenStats.filteredImageCount} blurry/unworthy image${tokenStats.filteredImageCount !== 1 ? 's' : ''} excluded`}
                size="small"
                color="default"
                title="Images flagged as blurry or not album-worthy were excluded from layout generation"
              />
            )}
            {tokenStats.includeAllQuality && (
              <Chip label="Quality filter OFF" size="small" color="warning" title="All images were included regardless of quality flags" />
            )}
          </Stack>
        )}
        {debugFileName && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
            <Chip
              icon={<JsonIcon />}
              label={debugFileName}
              size="small"
              variant="outlined"
              title="Debug log file written at workspace root"
              sx={{ fontFamily: 'monospace', fontSize: 11 }}
            />
          </Stack>
        )}

        {selectedLayoutAgentRuns.length > 0 && (
          <Paper variant="outlined" sx={{ mt: 1.25, p: 1.5, bgcolor: 'grey.50' }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Layout Agent Trace
              </Typography>
              <Chip size="small" color="secondary" variant="outlined" label={`${selectedLayoutAgentRuns.length} run(s) for selected scene`} />
              {selectedScene && (
                <Chip size="small" label={`${selectedScene.emoji || '📷'} ${selectedScene.sceneName || selectedScene.sceneId}`} />
              )}
            </Stack>

            <Stack spacing={1} sx={{ mt: 1.25 }}>
              {selectedLayoutAgentRuns.map((run, idx) => {
                const toolNames = Object.keys(run.toolCallsByName || {});
                const runKey = run._runKey || `run-${idx}`;
                const isExpanded = !!expandedLayoutAgentRuns[runKey];
                return (
                  <Box
                    key={runKey}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 1,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Chip size="small" label={`run ${idx + 1}`} />
                      {run.spreadIndex != null && <Chip size="small" variant="outlined" label={`spread ${run.spreadIndex}`} />}
                      <Chip size="small" variant="outlined" label={`submit attempts ${run.submitAttempts || run.attempts || 0}`} />
                      <Chip size="small" variant="outlined" label={`outer retries ${run.outerAttempts || 0}`} />
                      <Chip size="small" variant="outlined" label={`LLM iterations ${run.llmTurns || 0}`} />
                      <Chip size="small" variant="outlined" label={`tool calls ${run.toolCallCount || 0}`} />
                      {toolNames.length > 0 && (
                        <Tooltip title={toolNames.map(name => `${name}: ${run.toolCallsByName[name]}`).join(' | ')}>
                          <Chip
                            size="small"
                            color="info"
                            variant="outlined"
                            label={`tools used: ${toolNames.join(', ')}`}
                            sx={{ maxWidth: 440 }}
                          />
                        </Tooltip>
                      )}
                      <Box sx={{ flex: 1 }} />
                      <Tooltip title={isExpanded ? 'Hide transcript' : 'Show transcript'}>
                        <IconButton
                          size="small"
                          onClick={() => setExpandedLayoutAgentRuns(prev => ({ ...prev, [runKey]: !prev[runKey] }))}
                        >
                          {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </Stack>

                    <Collapse in={isExpanded}>
                      <Box
                        sx={{
                          mt: 1,
                          p: 1,
                          borderRadius: 1,
                          bgcolor: '#10151f',
                          color: '#d7e0ea',
                          fontFamily: 'monospace',
                          fontSize: 11,
                          whiteSpace: 'pre-wrap',
                          maxHeight: 320,
                          overflow: 'auto',
                        }}
                      >
                        {Array.isArray(run.transcript) && run.transcript.length > 0
                          ? run.transcript.join('\n')
                          : 'No transcript available for this run.'}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
            </Stack>
          </Paper>
        )}
      </Paper>

      {/* Scene selection table */}
      {selectableScenes.length > 0 && selectedSceneIds !== null && (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle2" sx={{ flex: 1 }}>
              {layoutSceneTableMode === 'raw'
                ? 'Step-5 scenes mapped to Step-6 plan entries'
                : 'Select scenes to generate layouts for'}
            </Typography>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="layout-scene-table-mode-label">Table mode</InputLabel>
              <Select
                labelId="layout-scene-table-mode-label"
                value={layoutSceneTableMode}
                label="Table mode"
                onChange={(e) => setLayoutSceneTableMode(String(e.target.value))}
              >
                <MenuItem value="plan">Plan entries (generation selection)</MenuItem>
                <MenuItem value="raw">Raw Step-5 scenes (mapping view)</MenuItem>
              </Select>
            </FormControl>
            {layoutSceneTableMode === 'plan' && (
              <>
            <Typography variant="caption" color="text.secondary">Min rating:</Typography>
            {[0, 5, 6, 7, 8, 9, 10].map(r => (
              <Chip
                key={r}
                size="small"
                label={r === 0 ? 'All' : `★ ${r}+`}
                onClick={() => {
                  setMinRateFilter(r);
                  // Auto-select scenes that meet the threshold; others stay visible but unchecked
                  if (r === 0) {
                    setSelectedSceneIds(new Set(selectableScenes.map(s => s.sceneId)));
                  } else {
                    setSelectedSceneIds(new Set(
                      selectableScenes
                        .filter(s => (s.themeRelevanceScore ?? 0) >= r)
                        .map(s => s.sceneId)
                    ));
                  }
                }}
                color={minRateFilter === r ? 'primary' : 'default'}
                variant={minRateFilter === r ? 'filled' : 'outlined'}
                sx={{ cursor: 'pointer' }}
              />
            ))}
            <Divider orientation="vertical" flexItem />
            <Button size="small" onClick={() => setSelectedSceneIds(new Set(selectableScenes.map(s => s.sceneId)))}>
              All
            </Button>
            <Button size="small" onClick={() => setSelectedSceneIds(new Set(selectableScenes.filter(s => !layoutById.has(s.sceneId) || failedScenes.some(f => f.sceneId === s.sceneId)).map(s => s.sceneId)))}>
              Missing / failed
            </Button>
            <Button size="small" color="inherit" onClick={() => setSelectedSceneIds(new Set())}>
              None
            </Button>
              </>
            )}
          </Stack>
          {layoutSceneTableMode === 'plan' ? (
            <Table size="small">
              <TableBody>
                {selectableScenes.map((scene, idx) => {
                  const hasLayout = layoutById.has(scene.sceneId);
                  const checked = selectedSceneIds.has(scene.sceneId);
                  const failedEntry = failedScenes.find(f => f.sceneId === scene.sceneId);
                  return (
                    <TableRow
                      key={scene.sceneId}
                      hover
                      onClick={() => setSelectedSceneIds(prev => {
                        const next = new Set(prev);
                        checked ? next.delete(scene.sceneId) : next.add(scene.sceneId);
                        return next;
                      })}
                      sx={{ cursor: 'pointer', opacity: generating ? 0.5 : 1, bgcolor: failedEntry ? 'error.50' : undefined }}
                    >
                      <TableCell sx={{ width: 32, color: 'text.disabled', fontSize: 11, py: 0.5, px: 1 }}>{idx + 1}</TableCell>
                      <TableCell padding="checkbox" sx={{ width: 36 }}>
                        <Checkbox size="small" checked={checked} readOnly />
                      </TableCell>
                      <TableCell sx={{ width: 28, fontSize: 18, p: 0.5 }}>{scene.emoji || '📷'}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>
                        <Stack direction="column" spacing={0}>
                          <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" useFlexGap>
                            <span>{scene.sceneName}</span>
                            {scene.planBudget != null && (
                              <Chip label={`${scene.planBudget}p`} size="small" color={{ 1: 'default', 2: 'info', 3: 'warning', 4: 'error' }[scene.planBudget] || 'default'} variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                            )}
                            {scene.planRole && (
                              <Chip label={scene.planRole} size="small" color={{ opening: 'secondary', buildup: 'primary', climax: 'error', transition: 'default', close: 'success' }[scene.planRole.toLowerCase()] || 'default'} variant="filled" sx={{ height: 18, fontSize: 10, textTransform: 'capitalize' }} />
                            )}
                          </Stack>
                          {Array.isArray(scene.mergeWith) && scene.mergeWith.length > 0 && (
                            <Typography variant="caption" color="info.main" sx={{ fontSize: 10, lineHeight: 1.3 }}>
                              + {scene.mergeWith.map(mid => {
                                const ms = story?.scenes?.find(s => s.sceneId === mid);
                                return ms?.sceneName || mid;
                              }).join(', ')}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: 12, whiteSpace: 'nowrap' }}>{scene.dateHint || ''}</TableCell>
                      <TableCell sx={{ width: 70 }}>
                        {scene.themeRelevanceScore != null && (
                          <Chip
                            label={`★ ${scene.themeRelevanceScore}/10`}
                            size="small"
                            color={scoreColor(scene.themeRelevanceScore)}
                            sx={{ height: 20, fontSize: 11 }}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ width: 90 }}>
                        {failedEntry
                          ? <Tooltip title={failedEntry.error || 'Layout generation failed'} placement="top" arrow>
                              <Chip label="⚠ failed" size="small" color="error" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                            </Tooltip>
                          : hasLayout
                            ? <Chip label="has layout" size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                            : <Chip label="no layout" size="small" variant="outlined" sx={{ height: 18, fontSize: 10, opacity: 0.5 }} />}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 1 }}>
                This view shows all Step-5 scenes and where each one maps in Step-6. Layout generation still runs on plan entries.
              </Alert>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ width: 32, fontWeight: 600, fontSize: 12 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Step-5 scene</TableCell>
                    <TableCell sx={{ width: 140, fontWeight: 600, fontSize: 12 }}>Date</TableCell>
                    <TableCell sx={{ width: 82, fontWeight: 600, fontSize: 12 }}>Rating</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Mapped Step-6 entry</TableCell>
                    <TableCell sx={{ width: 80, fontWeight: 600, fontSize: 12 }}>Type</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rawStep5SceneRows.map((row) => (
                    <TableRow key={row.sceneId} hover>
                      <TableCell sx={{ color: 'text.disabled', fontSize: 11, py: 0.5, px: 1 }}>{row.idx + 1}</TableCell>
                      <TableCell sx={{ py: 0.5 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography sx={{ width: 20 }}>{row.emoji}</Typography>
                          <Stack spacing={0}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.sceneName}</Typography>
                            <Typography variant="caption" color="text.secondary">{row.sceneId}</Typography>
                          </Stack>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: 12, whiteSpace: 'nowrap' }}>{row.dateHint}</TableCell>
                      <TableCell>
                        {row.themeRelevanceScore != null && (
                          <Chip
                            label={`★ ${row.themeRelevanceScore}/10`}
                            size="small"
                            color={scoreColor(row.themeRelevanceScore)}
                            sx={{ height: 20, fontSize: 11 }}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 0.5 }}>
                        {row.mappedPlanEntryId ? (
                          <Stack spacing={0}>
                            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.mappedPlanEntryName}</Typography>
                              {row.mappedPlanBudget != null && (
                                <Chip
                                  label={`${row.mappedPlanBudget}p`}
                                  size="small"
                                  variant="outlined"
                                  color={{ 1: 'default', 2: 'info', 3: 'warning', 4: 'error' }[row.mappedPlanBudget] || 'default'}
                                  sx={{ height: 18, fontSize: 10 }}
                                />
                              )}
                            </Stack>
                            <Typography variant="caption" color="text.secondary">{row.mappedPlanEntryId}</Typography>
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="error.main">Not mapped</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={row.mappedType === 'merged' ? 'warning' : row.mappedType === 'direct' ? 'success' : 'error'}
                          variant={row.mappedType === 'unmapped' ? 'outlined' : 'filled'}
                          label={row.mappedType}
                          sx={{ height: 18, fontSize: 10, textTransform: 'capitalize' }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </Paper>
      )}

      {genError && <Alert severity="error">{genError}</Alert>}
      {failedScenes.length > 0 && (
        <Alert
          severity={failedScenes.some(f => f.quotaError) ? 'warning' : 'error'}
          onClose={() => setFailedScenes([])}
        >
          {failedScenes.some(f => f.quotaError)
            ? `OpenAI quota exceeded — ${failedScenes.length} scene${failedScenes.length > 1 ? 's' : ''} were not generated: ${failedScenes.map(f => f.sceneName || f.sceneId).join(', ')}. Add credit to your OpenAI account and re-run those scenes.`
            : (() => {
                const errors = [...new Set(failedScenes.map(f => f.error).filter(Boolean))];
                const sceneList = failedScenes.map(f => f.sceneName || f.sceneId).join(', ');
                const errorSummary = errors.length === 1 ? errors[0] : errors.join('; ');
                return `${failedScenes.length} scene${failedScenes.length > 1 ? 's' : ''} failed to generate: ${sceneList}. ${errorSummary ? `Error: ${errorSummary}` : ''} Re-run those scenes.`;
              })()
          }
        </Alert>
      )}

        </Box>
      )}

      {/* Nav buttons */}
      <Box sx={{ display: 'flex', gap: 2, pt: 2 }}>
        {onBack && (
          <Button variant="outlined" startIcon={<ArrowBack />} onClick={onBack}>
            Back to Story
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          endIcon={<ArrowForward />}
          onClick={() => onNext({})}
          disabled={layouts.length === 0}
        >
          Continue to Preview
        </Button>
      </Box>

      {/* Already-running dialog */}
      <Dialog open={alreadyRunningOpen} onClose={() => setAlreadyRunningOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Generation already in progress
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            A layout generation is already running for this session
            {runningJobInfo?.current != null && runningJobInfo?.total > 0
              ? ` — ${runningJobInfo.current}/${runningJobInfo.total} scenes completed`
              : ''}
            .<br /><br />
            Would you like to monitor it here, or cancel it and start a new generation?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAlreadyRunningOpen(false); setGenerating(true); }}>
            Monitor existing
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={cancelling ? <CircularProgress size={16} /> : <CancelIcon />}
            disabled={cancelling}
            onClick={async () => {
              setAlreadyRunningOpen(false);
              setCancelling(true);
              try {
                await albumService.cancelAlbumLayout(sessionId);
                await new Promise(r => setTimeout(r, 800));
                setCancelling(false);
                handleGenerate();
              } catch { setCancelling(false); }
            }}
          >
            Cancel &amp; restart
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete layouts confirmation dialog */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          Delete layouts?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {(() => {
              const idsToDelete = selectedSceneIds && selectedSceneIds.size > 0
                ? [...selectedSceneIds].filter(id => layoutById.has(id))
                : layouts.map(l => l.sceneId);
              return (
                <>
                  This will permanently delete the layout for{' '}
                  <strong>{idsToDelete.length} scene{idsToDelete.length !== 1 ? 's' : ''}</strong>:
                  <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                    {idsToDelete.map(id => {
                      const sc = story?.scenes?.find(s => s.sceneId === id);
                      return <li key={id}>{sc?.emoji} {sc?.sceneName || id}</li>;
                    })}
                  </ul>
                  <br />The scenes will be marked as ungenerated and can be re-generated later.
                </>
              );
            })()}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => { setConfirmDeleteOpen(false); handleDeleteLayouts(); }}
          >
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Re-generate confirmation dialog */}
      <Dialog open={confirmRegenOpen} onClose={() => setConfirmRegenOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Re-generate existing layouts?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {(() => {
              const regenIds = selectedSceneIds ? [...selectedSceneIds].filter(id => layoutById.has(id)) : [];
              return (
                <>
                  <strong>{regenIds.length} scene{regenIds.length !== 1 ? 's' : ''}</strong> you selected already have layouts:
                  <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                    {regenIds.map(id => {
                      const sc = story?.scenes?.find(s => s.sceneId === id);
                      return <li key={id}>{sc?.emoji} {sc?.sceneName || id}</li>;
                    })}
                  </ul>
                  <br />Re-generating will replace them with new AI calls — this costs money.
                </>
              );
            })()}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRegenOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<AIIcon />}
            onClick={() => { setConfirmRegenOpen(false); handleGenerate(); }}
          >
            Yes, Re-generate
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ──────────────────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WizardStep6Layout;
